import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {Icon} from '@wandb/weave/components/Icon';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import {Maybe} from 'graphql/jsutils/Maybe';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Image} from 'semantic-ui-react';

import {
  useOrganizationMembersQuery,
  UserAccountType,
} from '../generated/graphql';
import {checkIsServiceAccount} from '../util/serviceAccounts';

export interface TeamInviteOption {
  value: string;
  label: string;
  username: Maybe<string>;
  photoUrl: Maybe<string>;
  name: Maybe<string>;
  email: Maybe<string>;
  accountType: Maybe<UserAccountType>;
}

export function useTeamInviteOptions(
  organizationName: string,
  teamName?: string
) {
  const {data: orgData, loading} = useOrganizationMembersQuery({
    variables: {orgName: organizationName},
    skip: !organizationName,
  });

  const options: TeamInviteOption[] = useMemo(() => {
    if (!orgData?.organization?.members) {
      return [];
    }

    return orgData.organization.members
      .filter(member => {
        if (checkIsServiceAccount(member.user.accountType)) {
          return false;
        }

        if (teamName && member.teams?.edges) {
          const isAlreadyInTeam = member.teams.edges.some(
            edge => edge.node?.name === teamName
          );
          if (isAlreadyInTeam) {
            return false;
          }
        }
        return true;
      })
      .map(member => ({
        value: member.user.id,
        label: member.username || member.name,
        username: member.username,
        photoUrl: member.photoUrl,
        name: member.name,
        email: member.email,
        accountType: member.user.accountType,
      }));
  }, [orgData?.organization?.members, teamName]);

  return {options, loading};
}

interface TeamInviteOptionsProps {
  emailOrUsername: string;
  setEmailOrUsername: (value: string) => void;
  organizationName: string;
  teamName?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const TeamInviteOptions: FC<TeamInviteOptionsProps> = ({
  emailOrUsername,
  setEmailOrUsername,
  organizationName,
  teamName,
  placeholder = 'Email or username',
}) => {
  const {options, loading} = useTeamInviteOptions(organizationName, teamName);

  const [inputValue, setInputValue] = useState(emailOrUsername || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showAllOptions, setShowAllOptions] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!inputValue) {
      return options;
    }

    const searchTerm = inputValue.toLowerCase();
    return options.filter(opt => {
      const name = opt.name?.toLowerCase() || '';
      const username = opt.username?.toLowerCase() || '';
      const email = opt.email?.toLowerCase() || '';

      return (
        name.includes(searchTerm) ||
        username.includes(searchTerm) ||
        email.includes(searchTerm)
      );
    });
  }, [options, inputValue]);

  // Sync with parent state
  useEffect(() => {
    if (emailOrUsername !== undefined) {
      setInputValue(emailOrUsername);
    }
  }, [emailOrUsername]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
    return;
  }, [showDropdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;

    setInputValue(value);
    setEmailOrUsername(value);
    setShowDropdown(true);
    setHighlightedIndex(-1);
    setShowAllOptions(false);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
    // Only show all options if input is empty, otherwise keep showing filtered results
    if (!inputValue) {
      setShowAllOptions(true);
    } else {
      setShowAllOptions(false);
    }
  };

  const handleInputBlur = () => {
    setEmailOrUsername(inputValue);

    // Small delay to allow click events to fire
    setTimeout(() => {
      setShowDropdown(false);
    }, 150);
  };

  const handleOptionClick = useCallback(
    (option: TeamInviteOption) => {
      const selectedValue = option.email || option.username || '';

      setInputValue(selectedValue);
      setEmailOrUsername(selectedValue);

      setShowDropdown(false);
      setHighlightedIndex(-1);
      setShowAllOptions(true);

      // Focus input and set cursor to end
      if (inputRef.current) {
        inputRef.current.focus();
        const len = selectedValue.length;
        inputRef.current.setSelectionRange(len, len);
      }
    },
    [setEmailOrUsername]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const displayedOptions = showAllOptions ? options : filteredOptions;
    if (!showDropdown) {
      return;
    }

    // Handle "Add" option when no matches found
    if (displayedOptions.length === 0 && inputValue) {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          setEmailOrUsername(inputValue);
          setShowDropdown(false);
          setHighlightedIndex(-1);
          setShowAllOptions(true);
          break;
        case 'Escape':
          e.preventDefault();
          setShowDropdown(false);
          setHighlightedIndex(-1);
          setInputValue('');
          break;
      }
      return;
    }

    if (displayedOptions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < displayedOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < displayedOptions.length
        ) {
          handleOptionClick(displayedOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setHighlightedIndex(-1);
        setInputValue('');
        break;
    }
  };

  return (
    <Tailwind>
      <div ref={containerRef} className="relative w-full">
        <div className="relative" data-test="add-member-input">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={clsx(
              'w-full rounded',
              'px-[14px] py-[9px]',
              'text-[16px] leading-[21px]',
              'bg-white dark:bg-moon',
              'text-moon-800 dark:text-white-50',
              'placeholder:text-[rgba(175,174,174,0.87)] dark:placeholder:text-moon-500',
              'appearance-none border-none',
              'outline outline-1 outline-moon-250 dark:outline-moon-250',
              'hover:outline-2 hover:outline-teal-350 dark:hover:outline-teal-300',
              'focus:outline-2 focus:outline-teal-400 dark:focus:outline-teal-300',
              'dark:selection:bg-moon-650 dark:selection:text-moon-200',
              'transition-none'
            )}
          />
        </div>

        {showDropdown &&
          ((showAllOptions ? options : filteredOptions).length > 0 ||
            (!showAllOptions && inputValue) ||
            loading) && (
            <div
              className="ui menu border-gray-200 shadow-sm visible absolute z-[1000] max-h-[400px] w-full overflow-y-auto rounded border transition"
              style={{top: 'calc(70%)'}}>
              {loading ? (
                <div
                  className="item disabled flex items-center"
                  style={{minHeight: '55px'}}>
                  <div className="flex w-full justify-center">
                    <WaveLoader size="small" />
                  </div>
                </div>
              ) : (showAllOptions ? options : filteredOptions).length > 0 ? (
                (showAllOptions ? options : filteredOptions).map(
                  (option, index) => (
                    <TeamInviteOptionItem
                      key={option.value}
                      option={option}
                      isHighlighted={index === highlightedIndex}
                      onClick={() => handleOptionClick(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onMouseLeave={() => setHighlightedIndex(-1)}
                    />
                  )
                )
              ) : (
                <div
                  className="item cursor-pointer w-full"
                  style={{
                    padding: '4px 4px',
                  }}
                  onClick={() => {
                    setEmailOrUsername(inputValue);
                    setShowDropdown(false);
                    setHighlightedIndex(-1);
                    setShowAllOptions(true);
                  }}
                  onMouseEnter={() => setHighlightedIndex(0)}
                  onMouseLeave={() => setHighlightedIndex(-1)}>
                  <div
                    className="flex items-center rounded w-full"
                    style={{
                      minHeight: '50px',
                      padding: '8px 12px',
                      backgroundColor:
                        highlightedIndex === 0
                          ? 'rgba(0, 0, 0, 0.05)'
                          : 'transparent',
                    }}>
                    <div className="flex items-center">
                      <div
                        className="ui circular image"
                        style={{
                          width: '35px',
                          height: '35px',
                          marginRight: '2px',
                        }}>
                        <div className="flex h-full w-full items-center justify-center rounded-full bg-gray-200">
                          <LegacyWBIcon
                            name="plus"
                            className="text-gray-600"
                            style={{fontSize: '22px', fontWeight: 'bold'}}
                          />
                        </div>
                      </div>
                      <span className="text-moon-700">
                        Add new user "{inputValue}"
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
      </div>
    </Tailwind>
  );
};

interface TeamInviteOptionItemProps {
  option: TeamInviteOption;
  isHighlighted: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const TeamInviteOptionItem: React.FC<TeamInviteOptionItemProps> = ({
  option,
  isHighlighted,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  return (
    <div
      className="item cursor-pointer w-full"
      style={{
        padding: '4px 4px',
      }}
      onClick={e => {
        e.preventDefault();
        onClick();
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <div
        className="flex items-center rounded w-full"
        style={{
          minHeight: '50px',
          padding: '8px 12px',
          backgroundColor: isHighlighted
            ? 'rgba(0, 0, 0, 0.05)'
            : 'transparent',
        }}>
        <TeamInviteOptionContent
          photoUrl={option.photoUrl}
          name={option.name}
          username={option.username}
          email={option.email}
          accountType={option.accountType}
        />
      </div>
    </div>
  );
};

export const TeamInviteOptionContent: React.FC<
  Pick<
    TeamInviteOption,
    'photoUrl' | 'username' | 'email' | 'name' | 'accountType'
  >
> = props => {
  const {photoUrl, username, email, name, accountType} = props;

  return (
    <div className={'flex items-center'}>
      {!checkIsServiceAccount(accountType) ? (
        <Image
          avatar
          src={photoUrl === '' ? '/default-profile-picture.png' : photoUrl}
        />
      ) : (
        <div className="mr-6 content-center justify-center rounded-full bg-teal-300 bg-opacity-[.48] p-6 align-middle">
          <Icon
            name="robot-service-member"
            className="h-20 w-20 text-teal-600"
          />
        </div>
      )}
      <div className={'flex flex-col items-center'}>
        <div className={'self-start'}>
          <span>{name}</span>
          {username && (
            <span className={'ml-6 text-sm text-moon-500'}>@{username}</span>
          )}
        </div>
        <span className={'-mt-2 self-start text-sm text-moon-500'}>
          {email}
        </span>
      </div>
    </div>
  );
};
